import { SelectOption } from '@globaleris/ui.library'
import {
  AnnexedParamModelPatchRequest,
  AnnexedParamModelPostRequest,
  AnnexedParamsModel,
  AnnexedParamsValuesModel
} from '../../../api/Argentia/endpoints/annexedParams/model/annexedParams'
import {
  AnnexedParamSectionForm,
  AnnexedParamSectionTableModel
} from './annexedParamAccordionSection.model'
import { EnumTipoValor } from '../../../api/oData/enums/enumTipoValor'
import moment from 'moment'

export const mapAnnexedParamsToSelectOption = (
  annexedParams: AnnexedParamsModel[]
): SelectOption[] => {
  return annexedParams.map(param => mapAnnexedParamToSelectOption(param))
}

export const mapAnnexedParamToSelectOption = (param: AnnexedParamsModel): SelectOption => ({
  label: param.detailedDescription ?? '',
  value: param.id ?? 0
})

export const mapAnnexedParamValuesToSelectOption = (
  annexedParams: AnnexedParamsValuesModel[]
): SelectOption[] => {
  return annexedParams.map(param => mapAnnexedParamValueToSelectOption(param))
}

export const mapAnnexedParamValueToSelectOption = (
  param: AnnexedParamsValuesModel
): SelectOption => ({
  label: `${param.code} - ${param.description ?? ''}`,
  value: param.id ?? 0
})

export const mapAnnexedParamFormToPostRequest = (
  formData: AnnexedParamSectionForm
): AnnexedParamModelPostRequest => {
  const request: AnnexedParamModelPostRequest = {
    annexedParam: formData.annexedParamId!,
    value: mapValueRequestByType(formData.valueTypeId!, formData)
  }

  return request
}

export const mapAnnexedParamFormToPatchRequest = (
  formData: AnnexedParamSectionForm
): AnnexedParamModelPatchRequest => {
  const request: AnnexedParamModelPatchRequest = {
    annexedEntityId: formData.annexedEntityId!,
    entityId: formData.entityId,
    annexedParam: formData.annexedParamId!,
    value: mapValueRequestByType(formData.valueTypeId!, formData)
  }

  return request
}

const mapValueRequestByType = (
  valueTypeId: EnumTipoValor,
  formData: AnnexedParamSectionForm
): string => {
  switch (valueTypeId) {
    case EnumTipoValor.Int:
      return formData.value_number?.toFixed(0) || ''
    case EnumTipoValor.Decimal:
      return formData.value_number?.toFixed(2) || ''
    case EnumTipoValor.Char:
    case EnumTipoValor.String:
      return formData.value_text || ''
    case EnumTipoValor.Bool:
      return formData.value_bool ? 'S' : 'N'
    case EnumTipoValor.DateTime:
      return moment(formData.value_date!).format('YYYY-MM-DD')
    case EnumTipoValor.List:
      return formData.value_list_label?.split(' - ')[0] || ''
    default:
      return formData.value_text || ''
  }
}

export const mapAnnexedParamFormDataToAnnexedParamTable = (
  formData: AnnexedParamSectionForm
): AnnexedParamSectionTableModel => {
  return {
    entityId: formData?.entityId,
    annexedEntityId: formData?.annexedEntityId,
    annexedParam: formData.annexedParamId,
    description: formData?.description,
    detailedDescription: formData?.detailedDescription,
    valueType: formData?.valueTypeId,
    value: mapValueRequestByType(formData.valueTypeId!, formData)
  }
}

export const mapSelectedRowToAnnexedParamForm = (
  selectedRow: AnnexedParamSectionTableModel
): AnnexedParamSectionForm => {
  let formData: AnnexedParamSectionForm = {
    description: selectedRow.description,
    detailedDescription: selectedRow.detailedDescription,
    annexedParamId: selectedRow.annexedParam,
    annexedEntityId: selectedRow.annexedEntityId,
    valueTypeId: selectedRow.valueType
  }

  switch (selectedRow.valueType) {
    case EnumTipoValor.Int:
    case EnumTipoValor.Decimal:
      formData = { ...formData, value_number: +selectedRow.value }
      break
    case EnumTipoValor.Char:
    case EnumTipoValor.String:
      formData = { ...formData, value_text: selectedRow.value }
      break
    case EnumTipoValor.Bool:
      formData = { ...formData, value_bool: selectedRow.value === 'S' ? true : false }
      break
    case EnumTipoValor.DateTime:
      formData = { ...formData, value_date: moment(selectedRow.value).toDate() }
      break
    case EnumTipoValor.List:
      formData = { ...formData, value_list_label: selectedRow.value }
      break
    default:
      formData = { ...formData, value_text: selectedRow.value }
      break
  }

  return formData
}

export const mapParamDescriptionsToTableData = (
  tableData: AnnexedParamSectionTableModel[],
  annexedParamsOptions: AnnexedParamsModel[]
): AnnexedParamSectionTableModel[] => {
  return tableData.map(tableRow => mapParamDescriptionsToTableRow(tableRow, annexedParamsOptions))
}

export const mapParamDescriptionsToTableRow = (
  tableRow: AnnexedParamSectionTableModel,
  annexedParamsOptions: AnnexedParamsModel[]
): AnnexedParamSectionTableModel => {
  const param = annexedParamsOptions.find(param => param.id === tableRow.annexedParam)

  if (param) {
    let formattedValue = tableRow.value

    if (param.valueTypeId === EnumTipoValor.List && param.annexedParamsValues) {
      const paramValue = param.annexedParamsValues.find(pv => pv.code === tableRow.value)
      if (paramValue) {
        formattedValue = `${paramValue.code} - ${paramValue.description ?? ''}`
      }
    }

    return {
      ...tableRow,
      description: param.description,
      detailedDescription: param.detailedDescription,
      valueType: param.valueTypeId!,
      value: formattedValue
    }
  }

  return tableRow
}
